import React, { createContext, useContext, useState, useEffect } from "react";
import { getEnv } from "../env-utils";
import axiosInstance from '../axiosInstanceSetup'

// Context for feature flags
const FeatureFlagContext = createContext({
  flags: {},
  isFeatureEnabled: () => false,
});

export const FeatureFlagProvider = ({ children }) => {
  const [flags, setFlags] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Parse | separated flags
  const parseFlags = (flagStr) => {
    const result = {};
    if (!flagStr) return result;

    // Split by '|' and mark each key as true
    flagStr.split("|").forEach((key) => {
      if (key.trim()) {
        result[key.trim().toLowerCase()] = true;
      }
    });

    return result;
  };

  // Fetch all flags on first mount
  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const response = await axiosInstance.get(`${process.env.REACT_APP_API_URL}/feature-flags`);
        const data = response.data;

        // normalize keys to avoid undefined lookups
        const normalized = {};
        Object.entries(data).forEach(([key, value]) => {
          normalized[key.trim().toLowerCase()] = value;
        });
        setFlags(normalized);
      } catch (err) {
        console.error("Failed to fetch feature flags", err);
      } finally {
        setLoaded(true);
      }
    };
    fetchFlags();
  }, []);

  const isFeatureEnabled = (feature_key) => {
    if (!loaded) return false; // guard async load

    const key = feature_key.toLowerCase().trim();
    const f = flags[key];
    if (!f) return false;

    // Global toggle
    if (!f.is_enabled) return false;

    // Environment override
    const environment = getEnv()?.toLowerCase();
    if (f.env_flags && environment) {
      const envFlags = parseFlags(f.env_flags);
      if (!envFlags[environment]) return false;
    }

    // Role override (single value)
    const role = localStorage.getItem("role")?.toLowerCase();
    if (f.role_flags && role) {
      const roleFlags = parseFlags(f.role_flags);
      if (!roleFlags[role]) return false;
    }

    return true;
  };

  return (
    <FeatureFlagContext.Provider value={{ flags, isFeatureEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Hook to use feature flags anywhere
export const useFeatureFlag = (feature_key) => {
  const { isFeatureEnabled } = useContext(FeatureFlagContext);
  return isFeatureEnabled(feature_key);
};

